import React, { useState, useEffect } from "react";
import {
  Stack,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Select
} from "@mui/material";

import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { format } from "date-fns";
import DownloadIcon from '@mui/icons-material/Download';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiFetchJson } from "../fetchWrapper";
import {
  formatMXDayString,
  formatMXddMM,
  formatMXDateTimeNoSeconds
} from "../util/DateUtil";

import { calculateHourlyPay, calculateCompletePayroll } from "../util/PayrollUtil";

export default function PayrollView() {
  const [employees, setEmployees] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [scheduledShifts, setScheduledShifts] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [weekOffset, setWeekOffset] = useState(0);
  const [dateRange, setDateRange] = useState(getLastFullWeek(0));
  const [hourlySalary, setHourlySalary] = useState(0);
  const [payPeriod, setPayPeriod] = useState("weekly");

  useEffect(() => {
    const fetchData = async () => {
      const [employeesRes, templatesRes] = await Promise.all([
        apiFetchJson("/api/employees"),
        apiFetchJson("/api/shift-templates"),
      ]);

      setEmployees(employeesRes);
      setShiftTemplates(templatesRes);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedEmployeeId) return;

    const startDate = format(dateRange.start, "yyyy-MM-dd");
    const endDate = format(dateRange.end, "yyyy-MM-dd");

    const employee = employees.find(e => e.id === selectedEmployeeId) || {};
    setSelectedEmployee(employee);

    // Calcular el salario por hora a partir del salario mensual
    if (employee.salary) {
      setHourlySalary(calculateHourlyPay(employee.salary));
    }

    const fetchData = async () => {
      const [scheduledShiftsRes, attendancesRes] = await Promise.all([
        apiFetchJson(
          `/api/scheduled-shifts?startDate=${startDate}&employeeId=${selectedEmployeeId}&endDate=${endDate}`
        ),
        apiFetchJson(
          `/api/attendances?startDate=${startDate}&employeeId=${selectedEmployeeId}&endDate=${endDate}`
        )
      ]);

      setScheduledShifts(scheduledShiftsRes);
      setAttendances(attendancesRes);
    };

    fetchData();
  }, [selectedEmployeeId, dateRange, employees]);

  function getLastFullWeek(offset = 0) {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;

    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);

    const start = new Date(monday);
    start.setDate(monday.getDate() - 7 + offset * 7);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return { start, end };
  }

  const changeWeek = (delta) => {
    const newOffset = weekOffset + delta;
    setWeekOffset(newOffset);
    setDateRange(getLastFullWeek(newOffset));
  };

  const resetToLastWeek = () => {
    setWeekOffset(0);
    setDateRange(getLastFullWeek(0));
  };

  const formatDate = (date) =>
    date.toISOString().split("T")[0];

  const getDaysInRange = (start, end) => {
    const dates = [];
    let current = new Date(start);

    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const days = getDaysInRange(dateRange.start, dateRange.end);

  const getShiftWithAttendanceForDay = (date) => {
    const key = date.toLocaleDateString("en-CA");

    return scheduledShifts
      .filter(s =>
        new Date(s.shiftDate).toLocaleDateString("en-CA") === key
      )
      .map(s => {
        const template = shiftTemplates.find(
          t => t.id === s.shiftTemplateId
        );

        const attendance = attendances.find(
          a => a.scheduledShiftId === s.id
        );

        return {
          shift: template || {},
          attendance: attendance || {}
        };
      });
  };

  const getHoursFoShift = (shift) => {
    return shift.reduce((sum, s) => {
      const { checkIn, checkOut } = s.attendance || {};
      if (!checkIn || !checkOut) return sum;

      let start = new Date(checkIn);
      let end = new Date(checkOut);

      let diff = end - start;
      if (diff < 0) diff += 86400000;

      return sum + diff;
    }, 0);
  };

  const groupByWeek = (days) => {
    const map = new Map();

    days.forEach((d) => {
      const date = new Date(d);
      const day = date.getDay();
      const diff = day === 0 ? -6 : 1 - day;

      const monday = new Date(date);
      monday.setDate(date.getDate() + diff);
      monday.setHours(0, 0, 0, 0);

      const key = monday.getTime();

      if (!map.has(key)) {
        map.set(key, { weekStart: monday, days: [] });
      }

      map.get(key).days.push(date);
    });

    return Array.from(map.values());
  };

  const weeks = groupByWeek(days);

  const handleExport = async () => {
    if (!selectedEmployee || !selectedEmployee.id) {
      alert("Por favor selecciona un empleado");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 10;

    // Header - Employee Info and Period
    const employeeName = `${selectedEmployee.user?.person?.firstname || ""} ${selectedEmployee.user?.person?.lastnameFirst || ""}`.trim();
    const dateRangeStr = `${format(dateRange.start, "dd/MM/yyyy")} - ${format(dateRange.end, "dd/MM/yyyy")}`;
    
    doc.setFontSize(16);
    doc.text("REPORTE DE NÓMINA", pageWidth / 2, yPosition, { align: "center" });
    yPosition += 10;

    doc.setFontSize(11);
    doc.text(`Empleado: ${employeeName}`, 10, yPosition);
    yPosition += 6;
    doc.text(`Período: ${dateRangeStr}`, 10, yPosition);
    yPosition += 6;
    doc.text(`Salario mensual: $${selectedEmployee.salary?.toFixed(2) || "N/A"} MXN`, 10, yPosition);
    yPosition += 6;
    doc.text(`Salario por hora: $${hourlySalary?.toFixed(2) || "N/A"} MXN`, 10, yPosition);
    yPosition += 10;

    // Preparar datos para la tabla
    const tableRows = [];
    let totalGross = 0;
    let totalISR = 0;
    let totalIMSS = 0;
    let totalNet = 0;

    weeks.forEach((week) => {
      let weekMs = 0;

      // Cabecera de la semana
      tableRows.push([
        {
          content: `Semana: ${week.weekStart.toLocaleDateString()}`,
          colSpan: 4,
          styles: { filled: true, fillColor: [220, 220, 220], fontStyle: "bold", halign: "center" }
        }
      ]);

      // Dias en la semana
      week.days.forEach((day) => {
        const shifts = getShiftWithAttendanceForDay(day);
        let shift;
        let shift2;
        if (shifts.length > 0) {
          shift = shifts[0];
          if (shifts.length > 1) {
            shift2 = shifts[1];
          }
        }

        const ms = getHoursFoShift(shifts);
        weekMs += ms;

        const h = Math.floor(ms / 3600000);
        const m = Math.floor((ms / 60000) % 60);

        tableRows.push([
          formatMXDayString(day) + ", " + formatMXddMM(day),
          shift ? formatMXDateTimeNoSeconds(shift?.attendance?.checkIn) : "",
          shift ? formatMXDateTimeNoSeconds(shift?.attendance?.checkOut) : "",
          `${h}:${m.toString().padStart(2, "0")}`
        ]);
      });

      // Calculaciones de nómina para la semana
      const payroll = calculateCompletePayroll(weekMs, hourlySalary, payPeriod);

      totalGross += payroll.gross;
      totalISR += payroll.tax.isr;
      totalIMSS += payroll.imss.imss;
      totalNet += payroll.net;

      tableRows.push([
        { content: "Total de horas", colSpan: 3, styles: { fontStyle: "bold" } },
        `${payroll.hours.toFixed(2)}h`
      ]);

      tableRows.push([
        { content: `Horas normales (${payroll.normalHours.toFixed(2)}h)`, colSpan: 3, styles: { fontStyle: "bold" } },
        `$${payroll.normalPay.toFixed(2)}`
      ]);

      if (payroll.extraHours > 0) {
        tableRows.push([
          { content: `Horas extras dobles (${payroll.extraHours.toFixed(2)}h)`, colSpan: 3, styles: { fontStyle: "bold" } },
          `$${payroll.doublePay.toFixed(2)}`
        ]);
      }

      if (payroll.tripleHours > 0) {
        tableRows.push([
          { content: `Horas extras triples (${payroll.tripleHours.toFixed(2)}h)`, colSpan: 3, styles: { fontStyle: "bold" } },
          `$${payroll.triplePay.toFixed(2)}`
        ]);
      }

      tableRows.push([
        { content: "Ingreso bruto", colSpan: 3, styles: { fontStyle: "bold", fillColor: [232, 245, 233] } },
        `$${payroll.gross.toFixed(2)}`
      ]);

      tableRows.push([
        { content: `ISR - Bracket: ${payroll.tax.bracket.porcentaje}% (${payroll.tax.bracket.limite_inferior.toFixed(0)} - ${payroll.tax.bracket.limite_superior.toFixed(0)})`, colSpan: 3 },
        `-$${payroll.tax.isr.toFixed(2)}`
      ]);

      tableRows.push([
        { content: `IMSS (${payroll.imss.rates.total}%) - Cesantía ${payroll.imss.rates.cesantia_vejez}% + Enfermedad ${payroll.imss.rates.enfermedad_maternidad}% + Invalidez ${payroll.imss.rates.invalidez_vida}%`, colSpan: 3 },
        `-$${payroll.imss.imss.toFixed(2)}`
      ]);

      tableRows.push([
        { content: "Neto semana", colSpan: 3, styles: { fontStyle: "bold", fillColor: [200, 230, 201] } },
        `$${payroll.net.toFixed(2)}`
      ]);

      // Separador
      tableRows.push([{ content: "", colSpan: 4 }]);
    });

    // Agregar totales al final de la tabla
    tableRows.push([
      { content: "TOTALES", colSpan: 3, styles: { fontStyle: "bold", fillColor: [100, 100, 100], textColor: [255, 255, 255] } },
      { content: "", styles: { fillColor: [100, 100, 100] } }
    ]);

    tableRows.push([
      { content: "Ingreso bruto total", colSpan: 3, styles: { fontStyle: "bold" } },
      `$${totalGross.toFixed(2)}`
    ]);

    tableRows.push([
      { content: "ISR total", colSpan: 3 },
      `-$${totalISR.toFixed(2)}`
    ]);

    tableRows.push([
      { content: "IMSS total", colSpan: 3 },
      `-$${totalIMSS.toFixed(2)}`
    ]);

    tableRows.push([
      { content: "NETO TOTAL", colSpan: 3, styles: { fontStyle: "bold", fillColor: [76, 175, 80], textColor: [255, 255, 255] } },
      `$${totalNet.toFixed(2)}`
    ]);

    // Crear tabla
    autoTable(doc, {
      head: [["Fecha", "CheckIn", "CheckOut", "Horas"]],
      body: tableRows,
      startY: yPosition,
      margin: { left: 10, right: 10 },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center"
      },
      bodyStyles: {
        cellPadding: 2,
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      }
    });

    // Generar nombre de archivo con el nombre del empleado y el rango de fechas
    const formatDateForFilename = (date) => format(date, "ddMMyyyy");
    const filename = `PayrollReport_${employeeName.replace(/\s+/g, "_")}_${formatDateForFilename(dateRange.start)}-${formatDateForFilename(dateRange.end)}.pdf`;

    // guardar PDF
    doc.save(filename);
  };

  const handleExportCsv = () => {
    if (!selectedEmployee || !selectedEmployee.id) {
      alert("Por favor selecciona un empleado");
      return;
    }

    const employeeName = `${selectedEmployee.user?.person?.firstname || ""} ${selectedEmployee.user?.person?.lastnameFirst || ""}`.trim();
    const dateRangeStr = `${format(dateRange.start, "dd/MM/yyyy")} - ${format(dateRange.end, "dd/MM/yyyy")}`;

    const csvRows = [];
    csvRows.push(["Reporte de Nómina"]);
    csvRows.push(["Empleado", employeeName]);
    csvRows.push(["Período", dateRangeStr]);
    csvRows.push(["Salario mensual", `$${selectedEmployee.salary?.toFixed(2) || "N/A"}`]);
    csvRows.push(["Salario hora", `$${(hourlySalary || 0).toFixed(2)}`]);
    csvRows.push([]);
    csvRows.push(["Fecha", "CheckIn", "CheckOut", "Horas"]);

    let totalGross = 0;
    let totalISR = 0;
    let totalIMSS = 0;
    let totalNet = 0;

    weeks.forEach((week) => {
      let weekMs = 0;

      csvRows.push([`Semana: ${week.weekStart.toLocaleDateString()}`, "", "", ""]);

      week.days.forEach((day) => {
        const shifts = getShiftWithAttendanceForDay(day);
        let shift;
        let shift2;
        if (shifts.length > 0) {
          shift = shifts[0];
          if (shifts.length > 1) {
            shift2 = shifts[1];
          }
        }

        const ms = getHoursFoShift(shifts);
        weekMs += ms;

        const h = Math.floor(ms / 3600000);
        const m = Math.floor((ms / 60000) % 60);

        const checkIn = shift ? formatMXDateTimeNoSeconds(shift?.attendance?.checkIn) : "";
        const checkOut = shift ? formatMXDateTimeNoSeconds(shift?.attendance?.checkOut) : "";

        csvRows.push([
          `${formatMXDayString(day)}, ${formatMXddMM(day)}`,
          checkIn,
          checkOut,
          `${h}:${m.toString().padStart(2, "0")}`,
        ]);
      });

      const payroll = calculateCompletePayroll(weekMs, hourlySalary, payPeriod);
      totalGross += payroll.gross;
      totalISR += payroll.tax.isr;
      totalIMSS += payroll.imss.imss;
      totalNet += payroll.net;

      csvRows.push(["Total de horas", `${payroll.hours.toFixed(2)}h`, "", ""]);
      csvRows.push([`Horas normales (${payroll.normalHours.toFixed(2)}h)`, `$${payroll.normalPay.toFixed(2)}`, "", ""]);
      if (payroll.extraHours > 0) {
        csvRows.push([`Horas extras dobles (${payroll.extraHours.toFixed(2)}h)`, `$${payroll.doublePay.toFixed(2)}`, "", ""]);
      }
      if (payroll.tripleHours > 0) {
        csvRows.push([`Horas extras triples (${payroll.tripleHours.toFixed(2)}h)`, `$${payroll.triplePay.toFixed(2)}`, "", ""]);
      }
      csvRows.push(["Ingreso bruto", `$${payroll.gross.toFixed(2)}`, "", ""]);
      csvRows.push([
        `ISR - Bracket: ${payroll.tax.bracket.porcentaje}% (${payroll.tax.bracket.limite_inferior.toFixed(0)} - ${payroll.tax.bracket.limite_superior.toFixed(0)})`,
        `-$${payroll.tax.isr.toFixed(2)}`,
        "",
        "",
      ]);
      csvRows.push([
        `IMSS (${payroll.imss.rates.total}%) - Cesantía ${payroll.imss.rates.cesantia_vejez}% + Enfermedad ${payroll.imss.rates.enfermedad_maternidad}% + Invalidez ${payroll.imss.rates.invalidez_vida}%`,
        `-$${payroll.imss.imss.toFixed(2)}`,
        "",
        "",
      ]);
      csvRows.push(["Neto semana", `$${payroll.net.toFixed(2)}`, "", ""]);
      csvRows.push([]);
    });

    csvRows.push(["TOTALES", "", "", ""]);
    csvRows.push(["Ingreso bruto total", `$${totalGross.toFixed(2)}`, "", ""]);
    csvRows.push(["ISR total", `-$${totalISR.toFixed(2)}`, "", ""]);
    csvRows.push(["IMSS total", `-$${totalIMSS.toFixed(2)}`, "", ""]);
    csvRows.push(["NETO TOTAL", `$${totalNet.toFixed(2)}`, "", ""]);

    const csvContent = csvRows
      .map((row) =>
        row
          .map((value) => `"${String(value || "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `nomina_${employeeName.replace(/\s+/g, "_")}_${format(dateRange.start, "ddMMyyyy")}-${format(dateRange.end, "ddMMyyyy")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={2}>

      <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Empleado</InputLabel>
          <Select
            value={selectedEmployeeId}
            label="Empleado"
            onChange={(e) => setSelectedEmployeeId(e.target.value)}
          >
            <MenuItem value="">
              <em>Ninguno</em>
            </MenuItem>

            {employees.map(emp => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.user.person.firstname}{" "}
                {emp.user.person.lastnameFirst}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Desde"
          type="date"
          value={formatDate(dateRange.start)}
          onChange={(e) =>
            setDateRange({
              ...dateRange,
              start: new Date(e.target.value),
            })
          }
          size="small"
        />

        <TextField
          label="Hasta"
          type="date"
          value={formatDate(dateRange.end)}
          onChange={(e) =>
            setDateRange({
              ...dateRange,
              end: new Date(e.target.value),
            })
          }
          size="small"
        />

        {/* SEMANA NAV */}
        <IconButton onClick={() => changeWeek(-1)}>
          <ArrowBack />
        </IconButton>

        <Button onClick={resetToLastWeek}>
          Semana pasada
        </Button>

        <IconButton onClick={() => changeWeek(1)}>
          <ArrowForward />
        </IconButton>

        {/* SALARIO INFO */}
        <Box sx={{ display: "flex", alignItems: "center", height: 40 }}>
          <Typography>
            Salario mensual: {selectedEmployee.salary} MXN
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", height: 40 }}>
          <Typography>
            Salario hora:{" "}
            {(hourlySalary || 0).toFixed(2)} MXN
          </Typography>
        </Box>

      </Stack>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        mr: 2
      }}>
        <Typography variant="h6">titulo</Typography>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
        }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExport}>
            Exportar PDF
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}>
            Exportar CSV
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell align="center">CheckIn</TableCell>
              <TableCell align="center">CheckOut</TableCell>
              <TableCell align="right">Horas</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {weeks.map((week, i) => {
              let weekMs = 0;

              return (
                <React.Fragment key={i}>

                  {/* WEEK HEADER */}
                  <TableRow sx={{ background: "#eee" }}>
                    <TableCell colSpan={4}>
                      Semana: {week.weekStart.toLocaleDateString()}
                    </TableCell>
                  </TableRow>

                  {/* DAYS */}
                  {week.days.map((day, j) => {
                    const shifts = getShiftWithAttendanceForDay(day);
                    let shift;
                    let shift2;
                    if (shifts.length > 0) {
                      shift = shifts[0];
                      if (shifts.length > 1) {
                        shift2 = shifts[1];
                      }
                    }

                    const ms = getHoursFoShift(shifts);
                    weekMs += ms;

                    const h = Math.floor(ms / 3600000);
                    const m = Math.floor((ms / 60000) % 60);

                    return (
                      <TableRow key={j}>
                        <TableCell>
                          {formatMXDayString(day)},{" "}
                          {formatMXddMM(day)}
                        </TableCell>

                        <TableCell align="center">
                          {shift ? formatMXDateTimeNoSeconds(shift?.attendance?.checkIn) : ""}
                          {shift2 && <br />}
                          {shift2 ? formatMXDateTimeNoSeconds(shift2?.attendance?.checkIn) : ""}
                        </TableCell>
                        <TableCell align="center">
                          {shift ? formatMXDateTimeNoSeconds(shift?.attendance?.checkOut) : ""}
                          {shift2 && <br />}
                          {shift2 ? formatMXDateTimeNoSeconds(shift2?.attendance?.checkOut) : ""}
                        </TableCell>

                        <TableCell align="right">
                          {h}:{m.toString().padStart(2, "0")}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* CALCULO DE NOMINA */}
                  {(() => {
                    const payroll = calculateCompletePayroll(
                      weekMs,
                      hourlySalary,
                      payPeriod
                    );

                    return (
                      <>
                        <TableRow>
                          <TableCell colSpan={3}>
                            <b>Total de horas</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>{payroll.hours.toFixed(2)}h</b>
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ background: "#f5f5f5" }}>
                          <TableCell colSpan={3}>
                            <b>Horas normales ({payroll.normalHours.toFixed(2)}h)</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>${payroll.normalPay.toFixed(2)}</b>
                          </TableCell>
                        </TableRow>

                        {payroll.extraHours > 0 && (
                          <TableRow sx={{ background: "#f5f5f5" }}>
                            <TableCell colSpan={3}>
                              <b>Horas extras dobles ({payroll.extraHours.toFixed(2)}h)</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>${payroll.doublePay.toFixed(2)}</b>
                            </TableCell>
                          </TableRow>
                        )}

                        {payroll.tripleHours > 0 && (
                          <TableRow sx={{ background: "#f5f5f5" }}>
                            <TableCell colSpan={3}>
                              <b>Horas extras triples ({payroll.tripleHours.toFixed(2)}h)</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>${payroll.triplePay.toFixed(2)}</b>
                            </TableCell>
                          </TableRow>
                        )}

                        <TableRow sx={{ background: "#e8f5e9" }}>
                          <TableCell colSpan={3}>
                            <b>Ingreso bruto</b>
                          </TableCell>
                          <TableCell align="right">
                            <b>${payroll.gross.toFixed(2)}</b>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={3}>
                            ISR (Impuesto Sobre la Renta)
                            <br />
                            <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "#666" }}>
                              Bracket: {payroll.tax.bracket.porcentaje}% ({payroll.tax.bracket.limite_inferior.toFixed(0)} - {payroll.tax.bracket.limite_superior.toFixed(0)} MXN/mes)
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            -${payroll.tax.isr.toFixed(2)}
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={3}>
                            IMSS (Seguro Social)
                            <br />
                            <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "#666" }}>
                              Cesantía {payroll.imss.rates.cesantia_vejez}% + Enfermedad {payroll.imss.rates.enfermedad_maternidad}% + Invalidez {payroll.imss.rates.invalidez_vida}% = {payroll.imss.rates.total}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            -${payroll.imss.imss.toFixed(2)}
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ background: "#c8e6c9" }}>
                          <TableCell colSpan={3}>
                            <b>Neto semana</b>
                          </TableCell>

                          <TableCell align="right">
                            <b>${payroll.net.toFixed(2)}</b>
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })()}

                </React.Fragment>
              );
            })}
          </TableBody>

        </Table>
      </TableContainer>

    </Stack>
  );
}